import { useEffect, type RefObject } from "react";

/**
 * Auto-resize a textarea to fit its content, clamped between minRows and maxRows.
 * Above maxRows, the textarea scrolls internally.
 *
 * Pair with inline style `{ resize: "none", overflowY: "auto" }` on the textarea.
 */
export function useAutoResizeTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  opts: { minRows?: number; maxRows?: number; lineHeight?: number } = {}
): void {
  const { minRows = 1, maxRows = 12, lineHeight = 22 } = opts;
  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    const minPx = lineHeight * minRows;
    const maxPx = lineHeight * maxRows + 16;
    if (!value) {
      // Empty content can't exceed minPx, so skip scrollHeight measurement.
      // This avoids a known flex-layout bug where scrollHeight reports the
      // parent's available space instead of content size on first paint after
      // remount of a flex-stretched chat panel.
      ta.style.height = minPx + "px";
      return;
    }
    ta.style.height = "auto";
    const next = Math.max(minPx, Math.min(ta.scrollHeight, maxPx));
    ta.style.height = next + "px";
  }, [ref, value, minRows, maxRows, lineHeight]);
}
